import { getTranslation } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import React from 'react';

import { TooltipPanel } from './TooltipPanel.component';
import { BillingTerm } from '../../../lib/Paddle';

type IconName = React.ComponentProps<typeof Icon>['name'];

// Tinted chip colors for the plan icon next to the title. The dark success
// variant keeps the leaf legible on its light tint.
const ICON_ACCENTS = {
  success: { tint: 'successXLight', hex: theme.colors.successDark },
  brandPrimary: { tint: 'brandPrimaryXLight', hex: theme.colors.brandPrimary },
  brandSecondary: { tint: 'brandSecondaryXLight', hex: theme.colors.brandSecondary },
} as const;

type PlanIcon = { name: IconName; accent: keyof typeof ICON_ACCENTS };

const AnnualPriceRow: React.FC<{
  annualBilledPrice: string;
  annualFullPrice?: string;
  inverse?: boolean;
  perUser?: boolean;
}> = ({ annualBilledPrice, annualFullPrice, inverse, perUser }) => {
  const textColor = inverse ? 'white' : 'secondary';
  let billedLabel: string;
  if (perUser) {
    billedLabel = getTranslation('billed annually per seat');
  } else {
    billedLabel = getTranslation('billed annually');
  }
  return (
    <Layout.Row align="center">
      {annualFullPrice ? (
        <>
          <OpenSans.Primary
            size="s-16"
            weight="light-300"
            color={textColor}
            style={{ textDecorationLine: 'line-through' }}
          >
            {annualFullPrice}
          </OpenSans.Primary>
          <Spacer.Horizontal size={6} />
        </>
      ) : null}
      <OpenSans.Primary size="s-16" weight="light-300" color={textColor}>
        {annualBilledPrice} {billedLabel}
      </OpenSans.Primary>
    </Layout.Row>
  );
};

const FeatureRow: React.FC<{
  feature: string;
  inverse?: boolean;
}> = ({ feature, inverse }) => {
  // The bright success green passes WCAG contrast on the dark card only;
  // light cards need the dark variant to reach 4.5:1.
  const checkColor = inverse ? theme.colors.success : theme.colors.successDark;
  return (
    <Layout.Row align py={4} role="listitem">
      <Icon name="checkmark-circle" size={24} color={checkColor} aria-hidden />
      <Spacer.Horizontal size={8} />
      <OpenSans.Primary size="s-16" color={inverse ? 'inverse' : 'primary'} weight="bold-700" grow>
        {feature}
      </OpenSans.Primary>
    </Layout.Row>
  );
};

/**
 * Marketing card for one plan in the billing purchase ladder. `inverse`
 * renders the dark featured variant; `featured` adds the "Most Popular"
 * pill above the card.
 */
export const PlanCard: React.FC<{
  title: string;
  description: string;
  price: string;
  cta: string;
  // Explains a CTA the viewer can't act on (e.g. "Not Applicable"), surfaced on hover from an
  // information symbol beside the button rather than as always-on copy.
  ctaInfo?: string;
  onPress: () => void;
  featureHeader: string;
  features: string[];
  icon?: PlanIcon;
  perUser?: boolean;
  inverse?: boolean;
  featured?: boolean;
  ctaDisabled?: boolean;
  term?: BillingTerm;
  annualBilledPrice?: string;
  annualFullPrice?: string;
  trialDays?: number;
  minUsersLabel?: string;
  footNote?: { iconName: IconName; title: string; description: string };
}> = ({
  title,
  description,
  price,
  cta,
  ctaInfo,
  onPress,
  featureHeader,
  features,
  icon,
  perUser,
  inverse,
  featured,
  ctaDisabled,
  term = 'monthly',
  annualBilledPrice,
  annualFullPrice,
  trialDays,
  minUsersLabel,
  footNote,
}) => {
  const [ctaInfoVisible, setCtaInfoVisible] = React.useState(false);

  return (
    <Layout.Column
      bg={inverse ? 'brandPrimaryDark' : 'white'}
      border={inverse ? undefined : [1, 'solid', 'grayLight']}
      radius={16}
      px
      py={20}
      style={{
        maxWidth: 350,
        minWidth: 300,
        position: 'relative',
        boxShadow: featured ? '0 12px 32px rgba(19, 105, 227, 0.18)' : undefined,
      }}
    >
      {featured ? (
        <Layout.Row style={{ position: 'absolute', top: -16, left: 0, right: 0 }} center>
          <Layout.Row
            bg="white"
            border={[1, 'solid', 'brandPrimaryDark']}
            radius={100}
            px={14}
            py={6}
            align
          >
            <Icon name="star" size={12} color={theme.colors.brandPrimaryDark} aria-hidden />
            <Spacer.Horizontal size={6} />
            <OpenSans.Primary size="xs-12" weight="bold-700" color="brandPrimaryDark">
              {getTranslation('MOST POPULAR')}
            </OpenSans.Primary>
          </Layout.Row>
        </Layout.Row>
      ) : null}

      <Layout.Row align>
        {icon ? (
          <>
            <Layout.Column bg={ICON_ACCENTS[icon.accent].tint} radius={12} px={10} py={10} center>
              <Icon name={icon.name} size={22} color={ICON_ACCENTS[icon.accent].hex} aria-hidden />
            </Layout.Column>
            <Spacer.Horizontal size={12} />
          </>
        ) : null}
        <Layout.Row bg={inverse ? 'white' : 'grayXLight'} radius={100} px py={2}>
          <OpenSans.Primary
            size="s-16"
            weight="bold-700"
            color={inverse ? 'brandPrimaryDark' : 'grayDark'}
            role="heading"
            aria-level={3}
          >
            {title}
          </OpenSans.Primary>
        </Layout.Row>
      </Layout.Row>

      <Spacer.Vertical size={16} />

      {/* Reserve two lines so prices and CTAs line up across the ladder. */}
      <OpenSans.Primary
        size="s-16"
        weight="light-300"
        color={inverse ? 'white' : 'secondary'}
        style={{ minHeight: 42 }}
      >
        {description}
      </OpenSans.Primary>

      <Spacer.Vertical size={16} />

      <Layout.Row align="flex-end">
        <OpenSans.Primary weight="bold-700" size={32} color={inverse ? 'white' : 'primary'}>
          {price}
        </OpenSans.Primary>
        <Spacer.Horizontal size={6} />
        <Layout.Column py={8}>
          {perUser ? (
            <OpenSans.Primary
              weight="light-300"
              color={inverse ? 'white' : 'primary'}
              size={'xs-12'}
            >
              {getTranslation('PER USER')}
            </OpenSans.Primary>
          ) : null}
          <OpenSans.Primary weight="light-300" color={inverse ? 'white' : 'primary'} size={'xs-12'}>
            {getTranslation('PER MONTH')}
          </OpenSans.Primary>
        </Layout.Column>
      </Layout.Row>

      {term === 'annual' && annualBilledPrice ? (
        <AnnualPriceRow
          annualBilledPrice={annualBilledPrice}
          annualFullPrice={annualFullPrice}
          inverse={inverse}
          perUser={perUser}
        />
      ) : null}

      {trialDays ? (
        <>
          <Spacer.Vertical size={8} />
          <Layout.Row align="center">
            <Icon
              name="checkmark-circle"
              size={16}
              color={inverse ? theme.colors.success : theme.colors.successDark}
              aria-hidden
            />
            <Spacer.Horizontal size={6} />
            <OpenSans.Primary
              size="s-16"
              weight="bold-700"
              color={inverse ? 'success' : 'successDark'}
            >
              {`${getTranslation('Free')} ${trialDays}-${getTranslation('day trial')}`}
            </OpenSans.Primary>
            <Spacer.Horizontal size={6} />
            <OpenSans.Primary
              size="s-16"
              weight="light-300"
              color={inverse ? 'white' : 'secondary'}
            >
              {`• ${getTranslation('Cancel anytime')}`}
            </OpenSans.Primary>
          </Layout.Row>
        </>
      ) : null}

      <Spacer.Vertical size={16} />

      <Layout.Row style={{ width: '100%', position: 'relative' }}>
        <Layout.PressableColumn
          bg={inverse ? 'white' : 'brandPrimaryXLight'}
          border={inverse ? undefined : [1, 'solid', 'brandPrimary']}
          px
          py
          radius={100}
          center
          onClick={() => {
            if (ctaDisabled) {
              return;
            }
            onPress();
          }}
          grow
          type="button"
          aria-label={ctaInfo ? `${cta} — ${title}. ${ctaInfo}` : `${cta} — ${title}`}
          aria-disabled={ctaDisabled}
          style={ctaDisabled ? { opacity: 0.55, cursor: 'default' } : undefined}
          onMouseEnter={() => setCtaInfoVisible(true)}
          onMouseLeave={() => setCtaInfoVisible(false)}
        >
          <Layout.Row align>
            <OpenSans.Pressable weight="bold-700">{cta}</OpenSans.Pressable>
            {ctaInfo ? (
              <>
                <Spacer.Horizontal size={6} />
                <Icon
                  name="information-circle-outline"
                  size={18}
                  color={theme.colors.brandPrimary}
                />
              </>
            ) : null}
          </Layout.Row>
        </Layout.PressableColumn>
        {/* The panel sits outside the button: nested, it would inherit the disabled button's
            dimming and fall below readable contrast. */}
        {ctaInfo && ctaInfoVisible ? <TooltipPanel text={ctaInfo} /> : null}
      </Layout.Row>

      {minUsersLabel ? (
        <>
          <Spacer.Vertical size={10} />
          <Layout.Row bg="brandPrimaryXLight" radius={100} py={6} center align>
            <Icon name="people" size={14} color={theme.colors.brandPrimary} aria-hidden />
            <Spacer.Horizontal size={6} />
            <OpenSans.Primary size="xs-12" weight="bold-700" color="brandPrimary">
              {minUsersLabel}
            </OpenSans.Primary>
          </Layout.Row>
        </>
      ) : null}

      <Spacer.Vertical size={16} />

      <OpenSans.Primary size="s-16" weight="bold-700" color={inverse ? 'white' : 'primary'}>
        {featureHeader}
      </OpenSans.Primary>

      <Spacer.Vertical size={4} />

      <Layout.Column grow role="list">
        {features.map((feature) => (
          <FeatureRow key={feature} feature={feature} inverse={inverse} />
        ))}
      </Layout.Column>

      {footNote ? (
        <>
          <Spacer.Vertical size={12} />
          <Layout.Column
            bg={inverse ? 'semiTransparentWhite' : 'grayXLight'}
            style={{ height: 1 }}
          />
          <Spacer.Vertical size={12} />
          <Layout.Row align>
            <Layout.Column bg="brandPrimaryXLight" radius={100} px={8} py={8} center>
              <Icon
                name={footNote.iconName}
                size={16}
                color={theme.colors.brandPrimary}
                aria-hidden
              />
            </Layout.Column>
            <Spacer.Horizontal size={10} />
            <Layout.Column style={{ flex: 1 }}>
              <OpenSans.Primary
                size="s-16"
                weight="bold-700"
                color={inverse ? 'inverse' : 'primary'}
              >
                {footNote.title}
              </OpenSans.Primary>
              <OpenSans.Primary size="xs-12" color={inverse ? 'white' : 'secondary'}>
                {footNote.description}
              </OpenSans.Primary>
            </Layout.Column>
          </Layout.Row>
        </>
      ) : null}
    </Layout.Column>
  );
};
